import { useState, useEffect } from 'react';
import { FileText, CheckCircle2, Clock, ChevronDown, ChevronUp, Plus, AlertCircle, Pencil } from 'lucide-react';
import api from '../../services/api';
import NoteTaskModal from './NoteTaskModal';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Strip HTML tags from Zoho rich-text fields and convert <br> to newlines. */
function stripHtml(html) {
  if (!html) return '';
  let text = html.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<[^>]+>/g, '');
  text = text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

function NoteItem({ note, onEdit, compact = false, expanded = false }) {
  return (
    <div
      className={`group cursor-pointer hover:bg-gray-50 rounded-lg transition-colors ${compact ? 'py-1 px-1' : 'border-l-2 border-orange-200 pl-3 py-1.5 mb-1.5'}`}
      onClick={() => onEdit(note)}
      role="button"
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          {!compact && note.title && <p className="text-[11px] font-semibold text-gray-600">{note.title}</p>}
          <p className={`${compact ? 'text-[11px]' : 'text-sm'} text-gray-800 leading-snug whitespace-pre-line ${(!expanded && !compact) ? 'line-clamp-2' : ''}`}>{stripHtml(note.content)}</p>
          <p className="text-[9px] text-gray-400 mt-0.5">{note.owner_name && `${note.owner_name} · `}{formatDate(note.created_at)}</p>
        </div>
        <Pencil className="w-3 h-3 text-gray-300 group-hover:text-orange-400 flex-shrink-0 mt-0.5 transition-colors print:hidden" />
      </div>
    </div>
  );
}

function TaskItem({ task, onEdit, compact = false }) {
  const isDone = task.status === 'Completed';

  let duePill = null;
  if (!isDone && task.due_date) {
    const today = new Date(); today.setHours(0,0,0,0);
    const due = new Date(task.due_date); due.setHours(0,0,0,0);
    const diff = (due - today) / 86400000;
    if (diff < 0) {
      duePill = <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-red-50 text-red-600 flex-shrink-0">Overdue</span>;
    } else if (diff === 0) {
      duePill = <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-amber-50 text-amber-600 flex-shrink-0">Due Today</span>;
    } else {
      duePill = <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-green-50 text-green-700 flex-shrink-0">Due {formatDate(task.due_date)}</span>;
    }
  }

  return (
    <div
      className={`group flex items-start gap-2 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors ${compact ? 'py-1 px-1' : 'py-1.5 px-1 border-b border-gray-50 last:border-0'}`}
      onClick={() => onEdit(task)}
      role="button"
    >
      {isDone
        ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
        : task.due_date && new Date(task.due_date) < new Date()
          ? <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
          : <Clock className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
      }
      <div className="flex-1 min-w-0">
        <p className={`${compact ? 'text-[11px]' : 'text-sm'} font-medium leading-snug ${isDone ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{task.subject}</p>
        {!compact && task.description && <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-line">{stripHtml(task.description)}</p>}
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {duePill}
          {!compact && task.priority && task.priority !== 'Normal' && <span className="text-[10px] text-amber-600 font-semibold">{task.priority}</span>}
          {task.owner_name && <span className="text-[10px] text-gray-400">{task.owner_name}</span>}
        </div>
      </div>
      <Pencil className="w-3 h-3 text-gray-300 group-hover:text-orange-400 transition-colors flex-shrink-0 mt-0.5 print:hidden" />
    </div>
  );
}

/**
 * Inline version (no outer card) — used inside PackageCard right column.
 * horizontal=true  → Tasks | Notes side by side, slim/compact
 * horizontal=false → Tasks then Notes stacked (project-level)
 */
export function InlineNotesTasksSection({ fetchNotesUrl, fetchTasksUrl, onAddNote, onAddTask, refreshNotesTick = 0, refreshTasksTick = 0, horizontal = false }) {
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [localNotesTick, setLocalNotesTick] = useState(0);
  const [localTasksTick, setLocalTasksTick] = useState(0);

  useEffect(() => {
    setNotesLoading(true);
    api.get(fetchNotesUrl).then(r => setNotes(r.data.notes || [])).catch(() => setNotes([])).finally(() => setNotesLoading(false));
  }, [fetchNotesUrl, refreshNotesTick, localNotesTick]);

  useEffect(() => {
    setTasksLoading(true);
    api.get(fetchTasksUrl).then(r => setTasks(r.data.tasks || [])).catch(() => setTasks([])).finally(() => setTasksLoading(false));
  }, [fetchTasksUrl, refreshTasksTick, localTasksTick]);

  // Sort: incomplete → nearest due date first (no date after dated) → completed last
  const sortedTasks = [...tasks].sort((a, b) => {
    const aDone = a.status === 'Completed', bDone = b.status === 'Completed';
    if (aDone !== bDone) return aDone ? 1 : -1;
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(a.due_date) - new Date(b.due_date);
  });

  // Notes: most-recent first (API returns desc, so no re-sort needed)
  const PREVIEW = horizontal ? 1 : 3;
  const visibleTasks = showAllTasks ? sortedTasks : sortedTasks.slice(0, PREVIEW);
  const visibleNotes = showAllNotes ? notes : notes.slice(0, PREVIEW);
  const moreTasks = sortedTasks.length - PREVIEW;
  const moreNotes = notes.length - PREVIEW;

  const handleEditSaved = () => {
    if (editTarget?.type === 'note') setLocalNotesTick(t => t + 1);
    else setLocalTasksTick(t => t + 1);
    setEditTarget(null);
  };

  const hasUpcoming = sortedTasks.some(t => t.status !== 'Completed');
  const pad = horizontal ? 'p-2.5' : 'p-4';
  const divider = horizontal ? 'divide-x-2 divide-gray-200' : 'divide-y divide-gray-100';
  const wrap = horizontal ? 'grid grid-cols-2' : 'flex flex-col';

  return (
    <div className={`h-full ${wrap} ${divider} bg-white`}>

      {/* ── Notes first ── */}
      <div className={pad}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-900 flex items-center gap-1">
            <FileText className="w-3 h-3" /> Notes ({notes.length})
          </span>
          <button onClick={onAddNote} className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-700 font-semibold print:hidden" data-testid="nt-add-note-btn">
            <Plus className="w-3 h-3" />Add
          </button>
        </div>
        {notesLoading ? (
          <div className="text-[10px] text-gray-400">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="text-[10px] text-gray-400">No notes yet</div>
        ) : (
          <div>
            {visibleNotes.map((n, idx) => (
              <NoteItem key={n.id} note={n} compact={horizontal} expanded={!horizontal && idx === 0} onEdit={item => setEditTarget({ type: 'note', item })} />
            ))}
            {moreNotes > 0 && (
              <button
                onClick={() => setShowAllNotes(v => !v)}
                className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-gray-500 hover:text-gray-700 transition-colors print:hidden"
              >
                {showAllNotes
                  ? <><ChevronUp className="w-3 h-3" />Show less</>
                  : <><ChevronDown className="w-3 h-3" /><span className="bg-gray-100 rounded-full px-1.5 py-0.5 text-[9px] font-bold">+{moreNotes}</span> more</>
                }
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Tasks second — yellow border when upcoming task exists ── */}
      <div
        className={`${pad} ${horizontal && hasUpcoming ? 'border-l-2 border-yellow-400' : ''}`}
        style={horizontal && hasUpcoming ? { borderLeftWidth: '2px', borderLeftColor: '#facc15' } : {}}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-900 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Tasks ({tasks.length})
          </span>
          <button onClick={onAddTask} className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-700 font-semibold print:hidden" data-testid="nt-add-task-btn">
            <Plus className="w-3 h-3" />Add
          </button>
        </div>
        {tasksLoading ? (
          <div className="text-[10px] text-gray-400">Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="text-[10px] text-gray-400">No tasks yet</div>
        ) : (
          <div>
            {visibleTasks.map(t => (
              <TaskItem key={t.id} task={t} compact={horizontal} onEdit={item => setEditTarget({ type: 'task', item })} />
            ))}
            {moreTasks > 0 && (
              <button
                onClick={() => setShowAllTasks(v => !v)}
                className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-gray-500 hover:text-gray-700 transition-colors print:hidden"
              >
                {showAllTasks
                  ? <><ChevronUp className="w-3 h-3" />Show less</>
                  : <><ChevronDown className="w-3 h-3" /><span className="bg-gray-100 rounded-full px-1.5 py-0.5 text-[9px] font-bold">+{moreTasks}</span> more</>
                }
              </button>
            )}
          </div>
        )}
      </div>

      {editTarget && (
        <NoteTaskModal
          type={editTarget.type}
          mode="edit"
          existing={editTarget.item}
          onClose={() => setEditTarget(null)}
          onSaved={handleEditSaved}
        />
      )}
    </div>
  );
}

/**
 * Full standalone card version — used for project-level notes & tasks.
 */
export default function NotesTasksSection({ title, fetchNotesUrl, fetchTasksUrl, createNoteUrl, createTaskTarget, onAddNote, onAddTask, refreshNotesTick = 0, refreshTasksTick = 0 }) {
  return (
    <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm notes-tasks-section">
      {title && <h4 className="text-sm font-bold text-gray-700 px-5 pt-5 pb-0">{title}</h4>}
      <InlineNotesTasksSection
        fetchNotesUrl={fetchNotesUrl}
        fetchTasksUrl={fetchTasksUrl}
        createNoteUrl={createNoteUrl}
        createTaskTarget={createTaskTarget}
        onAddNote={onAddNote}
        onAddTask={onAddTask}
        refreshNotesTick={refreshNotesTick}
        refreshTasksTick={refreshTasksTick}
      />
    </div>
  );
}
